import { useEffect, useRef, useState, type ClipboardEvent, type SyntheticEvent } from "react";
import { z } from "zod";

import Alert from "@mui/material/Alert";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Snackbar from "@mui/material/Snackbar";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

import { useQueryClient } from "@tanstack/react-query";

import { ApiError, apiPost } from "../api/client";
import { extractTimeoutConfirmTrackingId, mapRehydrationError } from "../api/errors";

const DEBOUNCE_MS = 500;
const MAX_TICKETS = 20;
// Separadores aceitos ao digitar manualmente + Enter: espaco, virgula, ponto-e-virgula.
const SPLIT_REGEX = /[\s,;]+/;
// Regex que extrai QUALQUER UUID valido de um texto arbitrario (logs, CSV, SQL output).
// Aceita qualquer versao (v1-v5) + case-insensitive. Nao ancora \b porque podemos
// ter UUIDs em meio a texto com pontuacao.
const UUID_EXTRACT_REGEX =
  /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;

const uuidSchema = z.string().uuid();

type RehydrateBatchResponse = { trackingId: string };

type ToastState =
  | { kind: "success"; message: string }
  | { kind: "error"; message: string };

type BatchTicketsFormProps = {
  onSubmitSuccess?: () => void;
};

/** Extrai apenas UUIDs validos do texto (descarta qualquer outra coisa). */
function extractUuids(input: string): string[] {
  const matches = input.match(UUID_EXTRACT_REGEX);
  return matches ?? [];
}

/** Split manual por separadores comuns (usado em Enter com valor digitado). */
function splitTokens(input: string): string[] {
  return input
    .split(SPLIT_REGEX)
    .map((t) => t.trim())
    .filter((t) => t.length > 0);
}

function dedupCaseInsensitive(tokens: string[]): { unique: string[]; duplicates: number } {
  const seen = new Set<string>();
  const unique: string[] = [];
  let duplicates = 0;
  for (const t of tokens) {
    const key = t.toLowerCase();
    if (seen.has(key)) {
      duplicates += 1;
      continue;
    }
    seen.add(key);
    unique.push(t);
  }
  return { unique, duplicates };
}

function isValidUuid(value: string): boolean {
  return uuidSchema.safeParse(value).success;
}

export function BatchTicketsForm({ onSubmitSuccess }: BatchTicketsFormProps) {
  const queryClient = useQueryClient();
  const [chips, setChips] = useState<string[]>([]);
  const [inputValue, setInputValue] = useState("");
  const [lastDuplicatesRemoved, setLastDuplicatesRemoved] = useState(0);
  const [lastIgnoredCount, setLastIgnoredCount] = useState(0);
  const [toast, setToast] = useState<ToastState | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const lastSubmitAtRef = useRef<number>(0);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Com a extracao por regex, todos os chips sao garantidamente UUIDs validos.
  const validCount = chips.length;
  const exceedsMax = chips.length > MAX_TICKETS;
  const allValid = !exceedsMax && validCount > 0;

  const submitDisabled = isSubmitting || exceedsMax || validCount === 0;

  const counterColor: "success.main" | "warning.main" | "text.secondary" =
    chips.length === 0 ? "text.secondary" : allValid ? "success.main" : "warning.main";

  /**
   * Processa mudancas do Autocomplete:
   * - Enter com valor digitado → extrai UUID se houver, senao ignora
   * - Remove chip via X → mantem estado, nao mexe em duplicates/ignored
   *
   * Garantimos que `chips` SEMPRE contem apenas UUIDs validos unicos.
   */
  const handleChange = (_event: SyntheticEvent, newValue: string[]) => {
    // Cada item em newValue pode ser: chip existente OU input digitado (freeSolo).
    // Rodamos o extractor em TUDO pra garantir invariante "so UUIDs validos".
    const extracted = newValue.flatMap((v) => {
      // Chip ja existente e UUID valido → preserva como esta
      if (isValidUuid(v)) return [v];
      // Input livre (ex: texto digitado antes do Enter): tenta extrair UUIDs
      return extractUuids(v);
    });

    const { unique, duplicates } = dedupCaseInsensitive(extracted);

    // Contador de "ignorados": tokens digitados que nao eram UUID validos.
    const tokensInNewInput = newValue.filter((v) => !chips.includes(v));
    const ignored = tokensInNewInput.reduce((acc, token) => {
      const tokens = splitTokens(token);
      const uuidsFound = extractUuids(token).length;
      return acc + Math.max(0, tokens.length - uuidsFound);
    }, 0);

    setChips(unique);
    setLastDuplicatesRemoved(duplicates);
    setLastIgnoredCount(ignored);
  };

  /**
   * Paste com lista grande: extrai UUIDs via regex, descarta qualquer lixo.
   * Aceita texto arbitrario — CSVs, logs, output de SQL, tudo funciona.
   */
  const handlePaste = (event: ClipboardEvent<HTMLDivElement>) => {
    const pasted = event.clipboardData.getData("text");
    if (!pasted) return;

    event.preventDefault();

    const foundUuids = extractUuids(pasted);
    // Conta ignorados: tokens non-empty que nao eram UUID
    const allTokens = splitTokens(pasted);
    const ignored = Math.max(0, allTokens.length - foundUuids.length);

    if (foundUuids.length === 0) {
      // Nada valido → avisa mas nao muda os chips atuais
      setLastIgnoredCount(ignored);
      setInputValue("");
      return;
    }

    const merged = [...chips, ...foundUuids];
    const { unique, duplicates } = dedupCaseInsensitive(merged);
    setChips(unique);
    setLastDuplicatesRemoved(duplicates);
    setLastIgnoredCount(ignored);
    setInputValue("");
  };

  const handleSubmit = async (event: SyntheticEvent) => {
    event.preventDefault();
    const now = Date.now();
    if (submitDisabled) return;
    if (now - lastSubmitAtRef.current < DEBOUNCE_MS) return;
    lastSubmitAtRef.current = now;

    const ticketIds = chips; // invariante: todos chips sao UUIDs validos
    setIsSubmitting(true);
    try {
      const response = await apiPost<RehydrateBatchResponse>(
        "/bff/api/v1/rehydrate/batch",
        { ticketIds },
      );
      if (!isMountedRef.current) return;

      await queryClient.invalidateQueries({ queryKey: ["operations"] });
      onSubmitSuccess?.();

      setChips([]);
      setInputValue("");
      setLastDuplicatesRemoved(0);
      setLastIgnoredCount(0);
      setToast({
        kind: "success",
        message: `Reidratação em lote iniciada (trackingId: ${response.trackingId})`,
      });
    } catch (err) {
      if (!isMountedRef.current) return;
      const recoveredTrackingId = extractTimeoutConfirmTrackingId(err);
      if (recoveredTrackingId) {
        await queryClient.invalidateQueries({ queryKey: ["operations"] });
        onSubmitSuccess?.();
        setChips([]);
        setInputValue("");
        setLastDuplicatesRemoved(0);
        setLastIgnoredCount(0);
        setToast({
          kind: "success",
          message: `Reidratação em lote enviada — aguardando confirmação (trackingId: ${recoveredTrackingId})`,
        });
      } else {
        // Erro terminal (409 com todos os tickets em conflito, 400 payload
        // inválido, etc) — limpa os chips + invalida a lista (o service já
        // pode ter persistido registro parcial).
        if (err instanceof ApiError && (err.status === 409 || err.status === 400)) {
          setChips([]);
          setInputValue("");
          setLastDuplicatesRemoved(0);
          setLastIgnoredCount(0);
          await queryClient.invalidateQueries({ queryKey: ["operations"] });
        }
        setToast({ kind: "error", message: mapRehydrationError(err) });
      }
    } finally {
      if (isMountedRef.current) {
        setIsSubmitting(false);
      }
    }
  };

  const handleCloseToast = () => setToast(null);

  const buttonLabel = isSubmitting
    ? "Enviando..."
    : validCount > 0
      ? `Reidratar ${validCount} ticket${validCount > 1 ? "s" : ""}`
      : "Reidratar";

  return (
    <Box component="section" aria-label="Reidratação em lote">
      <Stack spacing={2} component="form" noValidate onSubmit={handleSubmit}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Reidratar em lote
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Cole qualquer texto — apenas UUIDs válidos viram chips, o resto é ignorado.
          Até {MAX_TICKETS} tickets por envio. Use <kbd>Enter</kbd> para adicionar
          manualmente.
        </Typography>

        <Autocomplete
          multiple
          freeSolo
          disableClearable={false}
          options={[]}
          value={chips}
          inputValue={inputValue}
          onInputChange={(_e, v, reason) => {
            // Em paste nos deixamos o handler de paste cuidar — evita entrada dupla.
            if (reason === "input" || reason === "clear" || reason === "reset") {
              setInputValue(v);
            }
          }}
          onChange={handleChange}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => {
              const { key, ...tagProps } = getTagProps({ index });
              return (
                <Chip
                  key={key}
                  {...tagProps}
                  label={option}
                  size="small"
                  title={option}
                  sx={{
                    maxWidth: "100%",
                    "& .MuiChip-label": {
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    },
                  }}
                />
              );
            })
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label="Tickets"
              placeholder={
                chips.length === 0
                  ? "Cole ou digite os UUIDs dos tickets..."
                  : ""
              }
              onPaste={handlePaste}
              inputProps={{
                ...params.inputProps,
                "aria-label": "ticketIds",
                spellCheck: false,
                autoCapitalize: "off",
                autoCorrect: "off",
              }}
            />
          )}
          sx={{
            "& .MuiOutlinedInput-root": {
              alignContent: "flex-start",
              flexWrap: "wrap",
              minHeight: 120,
              padding: 1,
            },
          }}
        />

        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" aria-live="polite">
          <Typography variant="body2" sx={{ color: counterColor, fontWeight: 600 }}>
            {chips.length} ticket{chips.length === 1 ? "" : "s"} válido
            {chips.length === 1 ? "" : "s"}
          </Typography>
          {lastDuplicatesRemoved > 0 ? (
            <Typography variant="body2" color="text.secondary">
              • {lastDuplicatesRemoved} duplicado
              {lastDuplicatesRemoved > 1 ? "s" : ""} removido
              {lastDuplicatesRemoved > 1 ? "s" : ""}
            </Typography>
          ) : null}
          {lastIgnoredCount > 0 ? (
            <Typography variant="body2" color="text.secondary">
              • {lastIgnoredCount} token{lastIgnoredCount > 1 ? "s" : ""} não-UUID
              ignorado{lastIgnoredCount > 1 ? "s" : ""}
            </Typography>
          ) : null}
        </Stack>

        {exceedsMax ? (
          <Alert severity="error" role="alert">
            Máximo {MAX_TICKETS} tickets por lote. Você tem {chips.length}. Remova{" "}
            {chips.length - MAX_TICKETS} pra prosseguir.
          </Alert>
        ) : null}

        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <Button
            type="submit"
            variant="contained"
            color="secondary"
            size="large"
            disabled={submitDisabled}
            sx={{ fontWeight: 600, textTransform: "none", minWidth: 200 }}
            startIcon={
              isSubmitting ? <CircularProgress size={20} color="inherit" /> : undefined
            }
          >
            {buttonLabel}
          </Button>
        </Box>
      </Stack>

      {toast ? (
        <Snackbar
          open
          autoHideDuration={6000}
          onClose={handleCloseToast}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        >
          <Alert
            onClose={handleCloseToast}
            severity={toast.kind === "success" ? "success" : "error"}
            variant="filled"
            sx={{ width: "100%" }}
          >
            {toast.message}
          </Alert>
        </Snackbar>
      ) : null}
    </Box>
  );
}
